import React, { useMemo } from 'react';
import { Table, TableColumn } from '../../ui';
import { BomMissingField } from '../../../types/bom.types';
import { BomSheetDraft } from './useBomSheetDraft';
import { BomSheetField, SheetRow } from './types';
import { EditableCell, FIELD_LABEL, NumberCell, ReadCell, RemoveCell } from './BomSheetCells';
import RobbieSuggestedTag, { isRobbieSuggested } from '../RobbieSuggestedTag';

/**
 * The extracted-BOM sheet's grid: the v2 `Table` with an `Input` cell per
 * editable field. Edited cells tint cobalt, invalid ones danger; a line marked
 * for removal reads struck through with an undo. A viewer (or a line the
 * server never gave an id) gets the same columns as plain values.
 *
 * The draft is flattened once per render into plain per-row values, and the
 * columns are memoized on what they close over, so a keystroke re-renders
 * only the cells whose values changed (see `BomSheetCells`).
 */
export interface BomSheetTableProps {
  draft: BomSheetDraft;
  readOnly: boolean;
  disabled: boolean;
  /** Row key the last save refusal blamed — its number turns danger. */
  blamedKey?: string | null;
}

interface CellView {
  value: string;
  dirty: boolean;
  error?: string;
}

interface RowView {
  row: SheetRow;
  number: number;
  lineName: string;
  missing: string;
  cells: Record<BomSheetField, CellView>;
}

const FIELDS: Array<{ field: BomSheetField; width: string; mono?: boolean }> = [
  { field: 'name', width: 'minmax(150px,1.3fr)' },
  { field: 'partNumber', width: 'minmax(110px,0.8fr)' },
  { field: 'mpn', width: 'minmax(110px,0.8fr)' },
  { field: 'manufacturer', width: 'minmax(110px,0.8fr)' },
  { field: 'description', width: 'minmax(160px,1.4fr)' },
  { field: 'quantity', width: '64px', mono: true },
  { field: 'targetPrice', width: '96px', mono: true },
];

const MISSING_LABEL: Record<BomMissingField, string> = {
  name: 'name',
  quantity: 'quantity',
  partNumber: 'part number or MPN',
  description: 'description',
};

const useRowViews = (draft: BomSheetDraft): RowView[] => {
  const { rows, value, isDirty, errorFor, gapsFor } = draft;
  return useMemo(
    () =>
      rows.map((row, index) => {
        const gaps = row.kind === 'saved' ? gapsFor(row) : [];
        const cells = {} as Record<BomSheetField, CellView>;
        FIELDS.forEach(({ field }) => {
          cells[field] = { value: value(row, field), dirty: isDirty(row, field), error: errorFor(row, field) };
        });
        return {
          row,
          number: index + 1,
          lineName: cells.name.value.trim() || `line ${index + 1}`,
          missing: gaps.length > 0 ? `Missing ${gaps.map((g) => MISSING_LABEL[g]).join(', ')}` : '',
          cells,
        };
      }),
    [rows, value, isDirty, errorFor, gapsFor]
  );
};

export const BomSheetTable: React.FC<BomSheetTableProps> = ({ draft, readOnly, disabled, blamedKey }) => {
  const views = useRowViews(draft);
  const { setCell, toggleRemove } = draft;

  const columns = useMemo<TableColumn<RowView>[]>(() => {
    const number: TableColumn<RowView> = {
      key: 'no',
      header: '#',
      width: '38px',
      mono: true,
      render: (v) => <NumberCell number={v.number} blamed={blamedKey === v.row.key} missing={v.missing} />,
    };

    const fields = FIELDS.map<TableColumn<RowView>>(({ field, width, mono }) => ({
      key: field,
      header: FIELD_LABEL[field],
      width,
      align: mono ? 'right' : 'left',
      render: (v) => {
        const cell = v.cells[field];
        const content = readOnly || !v.row.editable || v.row.removed ? (
          <ReadCell field={field} value={cell.value} removed={v.row.removed} currency={v.row.original.currency} />
        ) : (
          <EditableCell
            row={v.row}
            field={field}
            value={cell.value}
            dirty={cell.dirty}
            error={cell.error}
            disabled={disabled}
            lineName={v.lineName}
            onChange={setCell}
          />
        );
        if (field !== 'name' || !isRobbieSuggested(v.row.original)) return content;
        return (
          <span className="flex min-w-0 items-center gap-s2">
            <span className="min-w-0 flex-1">{content}</span>
            <RobbieSuggestedTag />
          </span>
        );
      },
    }));

    const remove: TableColumn<RowView> = {
      key: 'actions',
      header: <span className="sr-only">Remove</span>,
      width: '28px',
      align: 'center',
      render: (v) =>
        v.row.editable ? (
          <RemoveCell
            row={v.row}
            removed={v.row.removed}
            disabled={disabled}
            lineName={v.lineName}
            onToggle={toggleRemove}
          />
        ) : null,
    };

    return [number, ...fields, ...(readOnly ? [] : [remove])];
  }, [readOnly, disabled, blamedKey, setCell, toggleRemove]);

  return (
    <div className="overflow-x-auto">
      <Table
        aria-label="Extracted BOM lines"
        columns={columns}
        rows={views}
        rowKey={(v) => v.row.key}
        density="compact"
        framed={false}
        stickyHeader
        maxHeight="56vh"
        className={readOnly ? 'min-w-[940px]' : 'min-w-[980px]'}
        empty="No components were extracted from your documents. Add a line to start one by hand."
      />
    </div>
  );
};

export default BomSheetTable;
